import AvailablePromoCodesModal from "@/features/Common/components/AvailablePromoCodesModal";
import Breakout from "@/features/Common/components/Breakout";
import DefaultError from "@/features/Common/components/DefaultError";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import { MedicalAssistanceDependantSelection } from "@/features/Common/components/MedicalAssistanceDependantSelection";
import OneVoucherRedemption from "@/features/Common/components/OneVoucherRedemption";
import PaymentOptionSelection from "@/features/Common/components/PaymentOptionsSelection";
import PaymentReceipt from "@/features/Common/components/PaymentReceipt";
import VoucherSelection from "@/features/Common/components/VoucherSelection";
import {
  getPathologyPaymentRedirectURL,
  Member,
  MemberProductPaymentRatesChargeCode,
  MemberProductPaymentRatesItem,
  MemberServiceOfferingType,
  MemberServiceOfferingUsage,
  PathologyPanel,
  VoucherFilterKey,
} from "@/services/core-api-adapter";
import { useGlobalStore } from "@/store";
import { Button, Stack, Typography } from "@mui/material";
import { useMachine } from "@xstate/react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { MembershipDependent } from "../../../../services/core-api-adapter";
import PathologyOrderSummary from "../PathologyOrderSummary";
import PathologyPanelDetails from "../PathologyPanelDetails";
import PathologyPanels from "../PathologyPanels";
import PathologyPaymentFailure from "../PathologyPaymentFailure";
import { PathologyPreparePrompt } from "../PathologyPreparePrompt";
import { PathologyPrompt } from "../PathologyPrompt";
import PathologyResultsBreakout from "../PathologyResultsBreakout";
import { eventNames, pathologyFlowStateMachine } from "./machine";
import {
  useInsightsAdapter,
  useTrackPaymentOnSuccess,
} from "@/services/insights-adapter";

export default function PathologyFlow() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { state } = useGlobalStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { trackSelectPathologyType } = useInsightsAdapter();

  const panelId = searchParams.get("panelId");
  const orderId = searchParams.get("orderId");
  const fromMyPurchasesPage = location.state?.fromMyPurchasesPage;

  const [pathologyStateMachine, pathologyStateMachineDispatch] = useMachine(
    pathologyFlowStateMachine,
    {
      input: {
        context: {
          panelId: panelId || null,
          orderId: orderId || null,
          currentUserMemberId: state.currentUser.memberId || null,
          fromMyPurchasesPage: fromMyPurchasesPage,
        },
      },
    } as any
  );

  useTrackPaymentOnSuccess({
    isSuccessState:
      pathologyStateMachine.matches("showingOrderSummary") &&
      !!pathologyStateMachine.context.paymentReceiptResponse?.paymentSuccess &&
      !!pathologyStateMachine.context.fetchOrderDetailsResponse,
    receipt: pathologyStateMachine.context.paymentReceiptResponse ?? null,
    chargeCode:
      pathologyStateMachine.context.fetchOrderDetailsResponse
        ?.panelChargeCode ?? "",
    memberId:
      pathologyStateMachine.context.fetchOrderDetailsResponse
        ?.testSubjectMemberId ?? "",
  });

  function onGoHomeButtonClick() {
    pathologyStateMachineDispatch({ type: eventNames.GO_HOME_BUTTON_CLICKED });
  }

  function onPaymentSuccess() {
    pathologyStateMachineDispatch({
      type: eventNames.PAYMENT_SUCCESS_RECEIVED,
    });
  }

  function onPanelClick(panel: PathologyPanel) {
    const memberId =
      pathologyStateMachine.context.selectedDependent?.memberId ||
      state.currentUser.memberId ||
      "";
    trackSelectPathologyType({
      serviceType: panel.panelChargeCode,
      member_id: memberId,
      event_datetime: new Date().toISOString(),
    });
    pathologyStateMachineDispatch({
      type: eventNames.BLOOD_PANEL_SELECTED,
      output: panel,
    });
  }

  function onBuyNowButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.BUY_NOW_BUTTON_CLICKED,
    });
  }

  function onBackButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.BACK_BUTTON_CLICKED,
    });
  }

  function onViewResultsClick() {
    pathologyStateMachineDispatch({
      type: eventNames.VIEW_RESULTS_BUTTON_CLICKED,
    });
  }

  function onContinueButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
    });
  }

  function onOneVoucherContinueButtonClick(oneVoucherPin: string) {
    pathologyStateMachineDispatch({
      type: eventNames.ONE_VOUCHER_CONTINUE_BUTTON_CLICKED,
      input: oneVoucherPin,
    });
  }

  function onViewPaymentReceiptClick() {
    pathologyStateMachineDispatch({
      type: eventNames.VIEW_RECEIPT_BUTTON_CLICKED,
    });
  }

  function onHowToPrepareClick() {
    pathologyStateMachineDispatch({
      type: eventNames.HOW_TO_PREPARE_BUTTON_CLICKED,
    });
  }

  function onDependentSelectionNextButtonClick(selectedMember: Member) {
    pathologyStateMachineDispatch({
      type: eventNames.DEPENDANT_SELECTED,
      input: selectedMember,
    });
  }

  function onBreakoutDisclaimerContinue() {
    pathologyStateMachineDispatch({
      type: eventNames.USER_REDIRECTED,
    });
  }

  function onStartDoctorConsultationClick() {
    navigate("/home/get-medical-assistance/intercare");
  }

  function onCardPaymentButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.CARD_PAYMENT_BUTTON_CLICKED,
    });
  }

  function getPaymentRedirectUrl() {
    const redirectURL = getPathologyPaymentRedirectURL(
      pathologyStateMachine.context.createOrderResponse?.orderId || ""
    );

    return redirectURL;
  }

  function onVoucherSelectionContinueClick(selectedVoucher?: string | null) {
    pathologyStateMachineDispatch({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
      input: selectedVoucher,
    });
  }

  function onVoucherButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.VOUCHER_BUTTON_CLICKED,
    });
  }

  function onOneVoucherButtonClick() {
    pathologyStateMachineDispatch({
      type: eventNames.ONE_VOUCHER_BUTTON_CLICKED,
    });
  }

  function onApplyDiscountVoucher(
    voucherCode: string,
    isUseVoucherLater?: boolean
  ) {
    pathologyStateMachineDispatch({
      type: eventNames.APPLY_DISCOUNT_VOUCHER,
      input: { voucherCode, isUseVoucherLater },
    });
  }

  function onAddDiscountVoucherClicked() {
    pathologyStateMachineDispatch({
      type: eventNames.ADD_A_DISCOUNT_VOUCHER_CLICKED,
    });
  }

  const directToDoctorPaymentRates =
    pathologyStateMachine.context?.memberProfileResponse?.consultation.consultationRates.find(
      (rate: MemberProductPaymentRatesItem) =>
        rate.chargeCode ===
        MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
    );

  function getOrderSummarySelectedDependent(): MembershipDependent | null {
    const memberId =
      pathologyStateMachine.context.fetchOrderDetailsResponse
        ?.testSubjectMemberId;

    const selectedDependent =
      pathologyStateMachine.context.selectedDependent || null;

    if (memberId === state.currentUser.memberId) {
      return {
        memberId: state.currentUser.memberId,
        firstName: state.currentUser.firstName,
        lastName: state.currentUser.lastName,
        identityDocumentType: null,
        identityDocumentValue: null,
        sexAtBirth: "",
        dateOfBirth: "",
        identityDocumentCountryOfIssue: "",
        status: "active",
      };
    }

    if (!selectedDependent) {
      return (
        pathologyStateMachine.context.membersList.find(
          (member) => member.memberId === memberId
        ) || null
      );
    }

    return selectedDependent;
  }

  function getFilteredServiceOfferingUsageItems(
    voucherKey: keyof MemberServiceOfferingUsage
  ): MemberServiceOfferingUsage[] {
    const selectedChargeCode =
      pathologyStateMachine.context.selectedBloodPanel?.panelChargeCode;

    const offeringId =
      pathologyStateMachine.context.memberServiceOfferingUsages?.find(
        (rate) => rate.chargeCode === selectedChargeCode
      )?.offeringId || "";

    if (!offeringId) {
      return (
        pathologyStateMachine.context.memberServiceOfferingUsages || []
      ).filter((item) => voucherKey in item && item[voucherKey] !== null);
    }

    return (
      pathologyStateMachine.context.memberServiceOfferingUsages || []
    ).filter(
      (item) =>
        (item.offeringId === offeringId ||
          item.serviceOfferingType ===
            MemberServiceOfferingType.ALL_SERVICES) &&
        voucherKey in item &&
        item[voucherKey] !== null
    );
  }

  function onDiscountVoucherSelected(
    selectedVoucher: MemberServiceOfferingUsage | null
  ) {
    pathologyStateMachineDispatch({
      type: eventNames.DISCOUNT_VOUCHER_SELECTED,
      input: selectedVoucher || null,
    });
  }

  return (
    <>
      {(pathologyStateMachine.matches("fetchingBloodPanelList") ||
        pathologyStateMachine.matches("creatingOrder") ||
        pathologyStateMachine.matches("waitingForOrderOutcome") ||
        pathologyStateMachine.matches("fetchingOrderDetails") ||
        pathologyStateMachine.matches("processingOrderDetailsOutcome") ||
        pathologyStateMachine.matches("waitingForOrderDetails") ||
        pathologyStateMachine.matches("processingPaymentOutcome") ||
        pathologyStateMachine.matches("fetchingPaymentReceipt") ||
        pathologyStateMachine.matches("updatingMemberDependents") ||
        pathologyStateMachine.matches("initiatingPayment") ||
        pathologyStateMachine.matches("fetchingMemberProfile") ||
        pathologyStateMachine.matches("showingPromptOrBloodPanelList") ||
        pathologyStateMachine.matches(
          "fetchingMemberDetailsOrFetchingPaymentReceipt"
        ) ||
        pathologyStateMachine.matches(
          "selectingDependantsFetchingBloodPanelListOrFetchingPaymentReceipt"
        ) ||
        pathologyStateMachine.matches(
          "showingBloodPanelListOrPromptingVoucherSelection"
        ) ||
        pathologyStateMachine.matches("fetchingBloodPanelDetails") ||
        pathologyStateMachine.matches("applyingDiscountVoucher") ||
        pathologyStateMachine.matches("refreshingPaymentServiceOfferings")) && (
        <FullscreenLoadingIndicator
          loadingText={t("common.pleaseWait")}
          mainIconType="face"
        >
          {(pathologyStateMachine.matches("waitingForOrderOutcome") ||
            pathologyStateMachine.matches("fetchingPaymentReceipt") ||
            pathologyStateMachine.matches("processingPaymentOutcome")) && (
            <Button variant="contained" onClick={onGoHomeButtonClick}>
              {t("common.goHomeButton")}
            </Button>
          )}
        </FullscreenLoadingIndicator>
      )}

      {(pathologyStateMachine.matches("showingBloodPanelList") ||
        pathologyStateMachine.matches("promptingVoucherSelection")) &&
        pathologyStateMachine.context?.memberProfileResponse && (
          <PathologyPanels
            panelList={pathologyStateMachine.context.bloodPanelListResponse}
            onPanelClick={onPanelClick}
            onStartDoctorConsultationClick={onStartDoctorConsultationClick}
            consultAmountFormatted={
              directToDoctorPaymentRates?.amountFormatted || ""
            }
            consultOriginalAmountFormatted={
              directToDoctorPaymentRates?.originalAmountFormatted || ""
            }
            isDiscountApplied={
              directToDoctorPaymentRates?.isDiscountApplied || false
            }
            selectedDependant={
              pathologyStateMachine.context.selectedDependent || null
            }
          />
        )}

      {pathologyStateMachine.matches("promptingVoucherSelection") &&
        pathologyStateMachine.context.memberServiceOfferingUsages && (
          <AvailablePromoCodesModal
            serviceOfferingUsageItems={
              pathologyStateMachine.context.memberServiceOfferingUsages
            }
            isModalOpen={true}
            onContinueButtonClick={onVoucherSelectionContinueClick}
            onSkipButtonClick={() =>
              pathologyStateMachineDispatch({
                type: eventNames.SKIP_BUTTON_CLICKED,
              })
            }
            onModalClose={onBackButtonClick}
          />
        )}

      {pathologyStateMachine.matches("selectingDependents") &&
        pathologyStateMachine.context.membersList && (
          <Stack p={2} height="100vh" justifyContent="end">
            <MedicalAssistanceDependantSelection
              onNextButtonClick={onDependentSelectionNextButtonClick}
              dependants={pathologyStateMachine.context.membersList}
            />
          </Stack>
        )}

      {pathologyStateMachine.matches("showingBloodPanelDetails") &&
        pathologyStateMachine.context.selectedBloodPanel && (
          <PathologyPanelDetails
            onBuyButtonClick={onBuyNowButtonClick}
            onBackButtonClick={onBackButtonClick}
            panel={pathologyStateMachine.context.selectedBloodPanel}
            selectedDependant={
              pathologyStateMachine.context.selectedDependent || null
            }
          />
        )}

      {pathologyStateMachine.matches("paymentFailed") && (
        <PathologyPaymentFailure
          onContinue={onGoHomeButtonClick}
          onPaymentSuccess={onPaymentSuccess}
          orderId={
            pathologyStateMachine.context?.orderIdQueryParam ||
            pathologyStateMachine.context?.createOrderResponse?.orderId ||
            ""
          }
        />
      )}

      {pathologyStateMachine.matches("showingPrompt") &&
        pathologyStateMachine.context.selectedBloodPanel && (
          <PathologyPrompt
            panel={pathologyStateMachine.context.selectedBloodPanel}
            onContinueButtonClick={onContinueButtonClick}
            onBackButtonClick={onBackButtonClick}
            selectedDependant={
              pathologyStateMachine.context.selectedDependent || null
            }
            isVoucherSelected={
              pathologyStateMachine.context.isVoucherApplied || false
            }
          />
        )}

      {pathologyStateMachine.matches("showingPaymentOptions") && (
        <PaymentOptionSelection
          onCardPaymentButtonClick={onCardPaymentButtonClick}
          onBack={onBackButtonClick}
          isBuyCareCardVoucher={false}
          onVoucherButtonClick={onVoucherButtonClick}
          onOneVoucherButtonClick={onOneVoucherButtonClick}
          serviceOfferingUsageItems={getFilteredServiceOfferingUsageItems(
            VoucherFilterKey.VoucherDetails
          )}
          discountVouchers={getFilteredServiceOfferingUsageItems(
            VoucherFilterKey.DiscountVoucherDetails
          )}
          selectedService={{
            chargeCode:
              pathologyStateMachine.context.selectedBloodPanel
                ?.panelChargeCode || "",
            amount:
              pathologyStateMachine.context.selectedBloodPanel
                ?.amountTotalFormatted || "",
          }}
          onDiscountVoucherSelected={onDiscountVoucherSelected}
          selectedDiscountVoucher={
            pathologyStateMachine.context.selectedDiscountVoucher
          }
          onApplyDiscountVoucher={onApplyDiscountVoucher}
          applyDiscountVoucherResponseError={
            pathologyStateMachine.context.applyDiscountVoucherResponseError
          }
          applyDiscountVoucherResponse={
            pathologyStateMachine.context.applyDiscountVoucherResponse
          }
          capturedDiscountVoucherCode={
            pathologyStateMachine.context.capturedDiscountVoucherCode
          }
          onAddDiscountVoucherClicked={onAddDiscountVoucherClicked}
          isUseVoucherLater={pathologyStateMachine.context.isUseVoucherLater}
        />
      )}

      {pathologyStateMachine.matches("selectingVoucher") && (
        <VoucherSelection
          onBackButtonClick={onBackButtonClick}
          onContinueButtonClick={onVoucherSelectionContinueClick}
          serviceOfferingUsageItems={getFilteredServiceOfferingUsageItems(
            VoucherFilterKey.VoucherDetails
          )}
        />
      )}

      {pathologyStateMachine.matches("showingOneVoucherRedemption") && (
        <OneVoucherRedemption
          serviceOfferingType="BloodTest"
          onBackButtonClick={onBackButtonClick}
          onOneVoucherContinueButtonClick={onOneVoucherContinueButtonClick}
          currentOneVoucherPin={
            pathologyStateMachine.context.oneVoucherPin || ""
          }
          responseError={
            pathologyStateMachine.context.oneVoucherRedemptionError || ""
          }
          discountAmount={
            pathologyStateMachine.context.selectedDiscountVoucher
              ?.discountVoucherDetails?.discountAmount || 0
          }
          selectedServiceAmount={
            pathologyStateMachine.context.selectedBloodPanel
              ?.amountTotalFormatted || ""
          }
        />
      )}

      {pathologyStateMachine.matches("breakoutScreen") && (
        <Stack sx={{ height: "98vh" }}>
          <Breakout
            type="Ecentric"
            onConfirm={onBreakoutDisclaimerContinue}
            redirectUrl={getPaymentRedirectUrl()}
            onDecline={onBackButtonClick}
          />
        </Stack>
      )}

      {pathologyStateMachine.matches("showingOrderSummary") &&
        pathologyStateMachine.context.fetchOrderDetailsResponse &&
        pathologyStateMachine.context.paymentReceiptResponse && (
          <PathologyOrderSummary
            order={pathologyStateMachine.context.fetchOrderDetailsResponse}
            paymentReceipt={
              pathologyStateMachine.context.paymentReceiptResponse
            }
            paymentDate={
              pathologyStateMachine.context.paymentReceiptResponse
                ?.paymentUtcDateTime
            }
            onViewResultsClick={onViewResultsClick}
            onViewReceiptClick={onViewPaymentReceiptClick}
            onBackButtonClick={onBackButtonClick}
            onHowToPrepareClick={onHowToPrepareClick}
            selectedDependant={getOrderSummarySelectedDependent()}
          />
        )}

      {pathologyStateMachine.matches("error") && (
        <Stack height="100vh" justifyContent="end">
          <DefaultError onComeBackLater={onGoHomeButtonClick}>
            <Typography variant="h2">{t("GenericError.title")}</Typography>
            <Typography pb={2}>{t("GenericError.content")}</Typography>
          </DefaultError>
        </Stack>
      )}

      {pathologyStateMachine.matches("showingPaymentReceipt") &&
        pathologyStateMachine.context.fetchOrderDetailsResponse &&
        pathologyStateMachine.context.paymentReceiptResponse && (
          <PaymentReceipt
            amount={
              pathologyStateMachine.context.paymentReceiptResponse.paymentAmount
            }
            currency={
              pathologyStateMachine.context.paymentReceiptResponse.currencyCode
            }
            transactionDate={
              pathologyStateMachine.context.paymentReceiptResponse
                .paymentUtcDateTime
            }
            referenceNumber={
              pathologyStateMachine.context.paymentReceiptResponse
                .transactionReferenceNumber
            }
            merchantId={
              pathologyStateMachine.context.paymentReceiptResponse.unuMerchantId
            }
            cardNumber={
              pathologyStateMachine.context.paymentReceiptResponse.cardNumber
            }
            onBackButtonClick={onBackButtonClick}
            isDiscountApplied={
              pathologyStateMachine.context.paymentReceiptResponse
                .isDiscountApplied
            }
            originalAmount={
              pathologyStateMachine.context.paymentReceiptResponse
                .originalAmount
            }
            voucherTypeName={
              pathologyStateMachine.context.paymentReceiptResponse.voucherName
            }
            voucherCode={
              pathologyStateMachine.context.paymentReceiptResponse.voucherCode
            }
            paymentMethod={
              pathologyStateMachine.context.paymentReceiptResponse.paymentMethod
            }
            oneVoucherDetails={
              pathologyStateMachine.context.paymentReceiptResponse
                .oneVoucherDetails
            }
          />
        )}

      {pathologyStateMachine.matches("showingHowToPreparePrompt") && (
        <PathologyPreparePrompt onBackButtonClick={onBackButtonClick} />
      )}

      {pathologyStateMachine.matches("showingWhatsappBreakout") && (
        <Stack height="100vh" p={2}>
          <PathologyResultsBreakout onBackButtonClick={onBackButtonClick} />
        </Stack>
      )}

      {pathologyStateMachine.matches("exitToMyPurchases") && (
        <Navigate
          to="/home/more/my-purchases"
          state={{ fromMyPurchasesPage: fromMyPurchasesPage ?? 1 }}
        />
      )}

      {pathologyStateMachine.matches("exit") && <Navigate to="/" />}
    </>
  );
}
